/**
 * Google 系列页的 **URL 状态**（2026-10-05 第二十五批 · 用户第 3 / 6 条；
 * 2026-10-12 用户截图第 1 条补 **时间范围**）。
 *
 * 进 URL 的字段：
 *  · `gq`     —— 系列名搜索（子串、大小写不敏感；空 = 不筛 → 不写 URL）；
 *  · `gview`  —— 概览 / 明细 切换（**默认 detail**，不写 URL，避免改变用户现有习惯）；
 *  · `grange` —— 时间范围档位（**默认 `last30`** = 用户要求的"默认最近 30 天"；等于默认不写 URL）；
 *  · `gfrom` / `gto` —— **`custom` 档位时**的生效区间（业务日 `YYYY-MM-DD`；档位的区间由
 *    `resolveDateRange` 现算 ⇒ 不写进 URL，见 `googleCampaignsSearchToParams` 的注释 ✓）。
 *
 * 为什么带 `g` 前缀：路由的 `stringifySearch` 靠「key 集合是否全部属于本页」来分派
 * 序列化器，而 `q` / `view` / `sort` / `range` / `from` / `to` 这些名字在广告管理 /
 * 指标表 / 花费总览里已经存在。用 `gq`/`gview`/`grange`/`gfrom`/`gto` 之后，本页的
 * key 集合与任何其它页**互斥**，链接不会串页（也正因如此**不能**直接复用
 * `rangeSearch.ts` 的 `from`/`to` 键 ✗ —— 只复用它的算法与纪律 ✓）。
 *
 * 解析对垃圾输入免疫（用户会手改 URL）：不认识的值一律回落默认，绝不抛错。
 */

import {
  dateRangeKey,
  isValidIsoDate,
  parseDateRangeKey,
  resolveDateRange,
  todayIso,
} from "./dateRange.js";

export type GoogleCampaignsView = "detail" | "overview";

/**
 * ⚠️ 字段名**就是 URL 参数名**（`gview` / `gq`）。
 *
 * 这不是偷懒：本仓库的路由 `stringifySearch` 靠「当前 search 对象的 key 集合是否
 * 全部属于本页」来挑选序列化器，而它拿到的正是 `validateSearch` 的返回值
 * （= 本接口）。因此 **state key 必须 == URL key**，否则分派会落到别的页（或落空），
 * 链接就串了。前缀 `g` 保证与广告管理 / 指标表 / 花费总览的 key 集合互斥。
 */
export interface GoogleCampaignsSearch {
  readonly gview: GoogleCampaignsView;
  readonly gq: string;
  /** 时间范围档位 key（`last30` / `last7` / `custom`…）。 */
  readonly grange: string;
  /** 生效区间左端（业务日；`custom` 时由用户填）。 */
  readonly gfrom: string;
  /** 生效区间右端（业务日）。 */
  readonly gto: string;
}

/** 本页独有的 URL key 集合（顺序无关；用于路由分派与测试）。 */
export const GOOGLE_CAMPAIGNS_SEARCH_KEYS: readonly string[] = [
  "gview",
  "gq",
  "grange",
  "gfrom",
  "gto",
];

/** 关键词长度上限（与后端 `q` 的 120 校验一致，超长直接截断而不是 400）。 */
export const GOOGLE_SEARCH_MAX_LENGTH = 120;

/**
 * 本页**默认时间范围** = 最近 30 天（用户截图第 1 条原话：「默认最近 30 天」）。
 *
 * ⚠️ 与全站默认 `DEFAULT_DATE_RANGE_KEY`（`last7`）**不同** —— 这是用户对**本页**
 * 的明确要求，别"统一"成 last7 ✗。
 */
export const DEFAULT_GOOGLE_RANGE_KEY = "last30";

const DEFAULT_PRESET = parseDateRangeKey(DEFAULT_GOOGLE_RANGE_KEY);

/**
 * 默认 URL 状态（`parseGoogleCampaignsSearch({})` 的期望值，也是序列化时的比较基准）。
 *
 * ⚠️ `gfrom`/`gto` 取**加载时**的今天：它们从不参与"等于默认就省略"的比较
 * （区间**总是写出** ✓），因此这里的取值只影响 `parse({})` 与它的 `toEqual` 断言。
 */
export const DEFAULT_GOOGLE_CAMPAIGNS_SEARCH: GoogleCampaignsSearch = {
  gview: "detail",
  gq: "",
  grange: DEFAULT_GOOGLE_RANGE_KEY,
  gfrom: todayIso(),
  gto: todayIso(),
};

export function parseGoogleCampaignsSearch(raw: Record<string, unknown>): GoogleCampaignsSearch {
  const viewRaw = raw["gview"];
  const qRaw = raw["gq"];
  const rangeRaw = raw["grange"];
  const fromRaw = raw["gfrom"];
  const toRaw = raw["gto"];
  const grange = typeof rangeRaw === "string" && rangeRaw !== "" ? rangeRaw : DEFAULT_GOOGLE_RANGE_KEY;
  // 区间缺省 = **今天**（由 `resolveGoogleCampaignsRange` 按档位现算覆盖）；
  // 只在 `custom` 且 URL 里带着合法日期时才会真正生效。
  const today = todayIso();
  return {
    gview: viewRaw === "overview" ? "overview" : "detail",
    gq: typeof qRaw === "string" ? qRaw.slice(0, GOOGLE_SEARCH_MAX_LENGTH) : "",
    grange,
    gfrom: typeof fromRaw === "string" && isValidIsoDate(fromRaw) ? fromRaw : today,
    gto: typeof toRaw === "string" && isValidIsoDate(toRaw) ? toRaw : today,
  };
}

/**
 * 生效区间（`grange` 档位 + 用户自填的 `gfrom`/`gto`）—— **页面与测试的唯一算法来源**。
 *
 * 口径与广告管理页一致（见 `dateRange.ts` 文件头）：
 *  · 「最近 N 天」= `[今天-N, 今天-1]`（**不含今天** ✓ —— 今天还在累计，含它会让同一个
 *    链接一天内给出不同数字，且最后一天必然偏低，看起来像"业绩掉了"）；
 *  · `custom` = 用 URL 里的 `gfrom`/`gto`（解析侧已保证是合法日期 ✓）；若区间倒置
 *    （`from > to`）⇒ **回落默认档** ✓（⛔ 不抛错、也不发一个必然 400 的请求 ✗）；
 *  · 档位不可识别 ⇒ `parseDateRangeKey` 自己回落 `last7` ✓（解析对垃圾免疫 ✓）。
 *
 * ⚠️ 这里**不接就绪度右端**：本页是 Google 视图，而契约的 `ReadinessViewKind` 只有
 * `cross_platform` / `meta_only` 两种（没有 google 变体）⇒ 不擅自新增视图类型 ✗；
 * 右端由后端按"已结算业务日"钉住并在响应里披露（`windowToClamped` ✓）。
 */
export function resolveGoogleCampaignsRange(
  search: GoogleCampaignsSearch,
  now: Date = new Date(),
): { readonly key: string; readonly from: string; readonly to: string } {
  // 兜底区间：**按调用时的 `now` 现算**（⛔ 不在模块加载时算死 ✗ —— 长期开着的标签页
  // 会拿到昨天的"今天"，默认区间整体漂一天）。
  const fallback = resolveDateRange(DEFAULT_PRESET, now);
  const toFallback = (): { readonly key: string; readonly from: string; readonly to: string } =>
    fallback === null
      ? { key: DEFAULT_GOOGLE_RANGE_KEY, from: search.gfrom, to: search.gto }
      : { key: DEFAULT_GOOGLE_RANGE_KEY, from: fallback.from, to: fallback.to };

  const preset = parseDateRangeKey(search.grange);
  if (preset.kind === "custom") {
    // 区间倒置 ⇒ 回落默认档（⛔ 不抛错、也不发一个必然 400 的请求 ✗）。
    if (search.gfrom > search.gto) return toFallback();
    return { key: "custom", from: search.gfrom, to: search.gto };
  }
  /**
   * ⚠️ 档位**不可识别**（手改 URL）⇒ 用**本页默认档**（`last30`），
   * 而不是 `parseDateRangeKey` 的全站兜底 `last7` ✗ —— 本页默认是 30 天（用户要求 ✓），
   * 落到 7 天会让"刷新一下区间就变了"看起来像 bug ✗。
   *
   * 判据 = "解析回来的档位名 ≠ URL 里写的那个"（`last7`/`month:2026-03` 等**合法**别名
   * 会与自己的规范名相等 ✓ 因而保留；`???` 之类会被判为不可识别 ✓）。
   */
  if (dateRangeKey(preset) !== search.grange.trim()) return toFallback();
  const resolved = resolveDateRange(preset, now);
  if (resolved === null) return toFallback();
  return { key: dateRangeKey(preset), from: resolved.from, to: resolved.to };
}

/** 点某个档位 → 要写进 URL 的补丁（语义照 `rangeSearch.ts` 的 `rangeSearchPickPatch` ✓）。 */
export function googleCampaignsRangePickPatch(
  key: string,
  now: Date = new Date(),
): Partial<GoogleCampaignsSearch> {
  const preset = parseDateRangeKey(key);
  // `custom`：只切档位、**保留当前区间**，让用户接着改输入框（否则会被清空 ✗）。
  if (preset.kind === "custom") return { grange: "custom" };
  const resolved = resolveDateRange(preset, now);
  if (resolved === null) return { grange: key };
  return { grange: key, gfrom: resolved.from, gto: resolved.to };
}

/** 序列化为 query 参数；等于默认值的字段一律省略（URL 短、可手写）。 */
export function googleCampaignsSearchToParams(search: GoogleCampaignsSearch): Record<string, string> {
  const params: Record<string, string> = {};
  if (search.gview !== DEFAULT_GOOGLE_CAMPAIGNS_SEARCH.gview) params["gview"] = search.gview;
  if (search.gq !== DEFAULT_GOOGLE_CAMPAIGNS_SEARCH.gq) params["gq"] = search.gq;
  if (search.grange !== DEFAULT_GOOGLE_CAMPAIGNS_SEARCH.grange) params["grange"] = search.grange;
  /**
   * ⚠️ 区间**只在 `custom` 时写出**（与 `rangeSearch.ts` 的"总是写出"不同，理由如下）：
   *
   *  · 档位（`last30`/`last90`…）的区间是**纯函数**——由档位 + "今天"决定
   *    （`resolveDateRange` ✓）⇒ 写进 URL 是冗余信息 ✗，还会让**默认 URL 不再为空**，
   *    破坏"默认不写出"的既有纪律（分享出去的链接也就不能"跟着看的人当天"解析了 ✗）；
   *  · `custom` 的区间**只存在于 URL**（没有别的来源）⇒ 必须写出 ✓，否则刷新就丢了。
   */
  if (search.grange === "custom") {
    params["gfrom"] = search.gfrom;
    params["gto"] = search.gto;
  }
  return params;
}

/** 供 TanStack Router 的 `stringifySearch` 使用：输出 `?gview=overview&gq=…&gfrom=…&gto=…`。 */
export function googleCampaignsSearchToQueryString(search: object): string {
  const params = googleCampaignsSearchToParams(parseGoogleCampaignsSearch(search as Record<string, unknown>));
  const usp = new URLSearchParams(params);
  const qs = usp.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

/** 是否处于「已筛选」状态（用于提示条 / 清除按钮）。 */
export function hasGoogleCampaignsFilter(search: GoogleCampaignsSearch): boolean {
  return search.gq.trim() !== "";
}
